import React, {useContext} from 'react'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import SearchSave, {searchSaveTrackingOrigins, searchSaveTypes} from '@adv-mt/mt-search-save'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import useContactSellerFormContext from '../../hooks/useContactSellerFormContext.js'
import PanelInstantOffer from './PanelInstantOffer.js'
import PanelPublishYourAd from './PanelPublishYourAd.js'

const ContactSellerConfirmation = () => {
  const {domain, i18n} = useContext(Context)
  const {track: analyticsTrack} = useAnalyticsTrack()
  const delayToNextFrame = useDelayToNextFrame()
  const config = domain.get('config')
  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')
  const {IS_INSTANT_OFFER_FEATURE_ENABLED} = config.get('seller')
  const {state, currentProps} = useContactSellerFormContext()
  const {isSubscriptionLead, province, redirectTo, search, showSearchSave, vehicle} = currentProps
  const isMobile = config.get('isMobile')
  const osName = config.get('osName')
  const {SUGGEST_AFTER_LEAD_ENABLED, MOBILE_ASSET_PATH, MOBILE_INSTALL_URL, DESKTOP_QR_CODE_PATH} =
    config.get('webToApp')

  const {
    HIT_ORIGINS: hitOrigins,
    TRACKING_EVENT_NAMES: trackingEventNames,
    TRACKING_OS_NAMES: trackingOsNames
  } = config.get('tracking')

  const hasSearch = Boolean(search)
  const hasRedirectTo = Boolean(redirectTo)

  const textCreateAlert = htmlStringToReactElement(
    i18n.t('CONTACT_SELLER.CONFIRMATION.CREATE_ALERT', {
      tagStart: '<strong>',
      tagEnd: '</strong>',
      vehicle,
      province
    })
  )

  const showCreateAlert = hasSearch && hasRedirectTo && showSearchSave && isSubscriptionLead

  const handleOnClickDownloadButton = () => {
    delayToNextFrame(() => {
      const eventData = {
        hit_origin: hitOrigins.APP_DOWNLOAD_FROM_POST_LEAD,
        hit_information: trackingOsNames[osName]
      }
      analyticsTrack({event: trackingEventNames.APP_DOWNLOAD_BUTTON_CLICKED, data: eventData})
    })
  }

  const userHasContactedNotification = () => {
    const notificationClass = 'mt-FormContactSeller-messageSent'
    const messageSentClass = `${notificationClass}Text`

    const MessageSentTitle = () => <strong>{i18n.t('LEAD.MESSAGE_SENT_TITLE')}</strong>
    const UIWithoutAppSuggestion = () => (
      <>
        <div className={`${notificationClass} ${notificationClass}--withoutAppSuggestion`}>
          <MessageSentTitle />
        </div>
        {state.isFinancingLead && <p>{i18n.t('LEAD.FINANCING_LEAD_MESSAGE')}</p>}
      </>
    )

    const MobileUI = () => {
      const UIWithAppSuggestion = () => {
        return (
          <>
            <div className={notificationClass}>
              <AtomImage src={`${URL_STATICS_IMAGES}${MOBILE_ASSET_PATH}`} alt="" />
              <div>
                <MessageSentTitle />
                <p className={messageSentClass}>
                  {htmlStringToReactElement(i18n.t('LEAD.MESSAGE_SENT_DESCRIPTION_MOBILE'))}
                </p>
              </div>
            </div>
            <AtomButton
              color={atomButtonColors.PRIMARY}
              design={atomButtonDesigns.SOLID}
              fullWidth
              href={MOBILE_INSTALL_URL}
              link
              shape={atomButtonShapes.CIRCULAR}
              size={atomButtonSizes.LARGE}
              target="_blank"
              onClick={handleOnClickDownloadButton}
            >
              {i18n.t('LEAD.APP_DOWNLOAD')}
            </AtomButton>
            {state.isFinancingLead && <p>{i18n.t('LEAD.FINANCING_LEAD_MESSAGE')}</p>}
          </>
        )
      }

      return SUGGEST_AFTER_LEAD_ENABLED ? <UIWithAppSuggestion /> : <UIWithoutAppSuggestion />
    }
    const DesktopUI = () => {
      const UIWithAppSuggestion = () => {
        return (
          <>
            <div className={notificationClass}>
              <div>
                <MessageSentTitle />
                <p className={messageSentClass}>
                  {htmlStringToReactElement(i18n.t('LEAD.MESSAGE_SENT_DESCRIPTION_DESKTOP'))}
                </p>
              </div>
              <AtomImage
                src={`${URL_STATICS_IMAGES}${DESKTOP_QR_CODE_PATH}`}
                alt={i18n.t('LEAD.QR_CODE_ALT')}
                className="mt-FormContactSeller-messageSentQr"
              />
            </div>
            {state.isFinancingLead && <p>{i18n.t('LEAD.FINANCING_LEAD_MESSAGE')}</p>}
          </>
        )
      }

      return SUGGEST_AFTER_LEAD_ENABLED ? <UIWithAppSuggestion /> : <UIWithoutAppSuggestion />
    }

    return (
      <div className={`mt-FormContactSeller-contactConfirmation-message`}>
        {isMobile ? <MobileUI /> : <DesktopUI />}
      </div>
    )
  }

  const SellerPanel = IS_INSTANT_OFFER_FEATURE_ENABLED ? PanelInstantOffer : PanelPublishYourAd

  return (
    <div className={'mt-FormContactSeller-contactConfirmation'}>
      {userHasContactedNotification()}
      {showCreateAlert && (
        <div className="mt-FormContactSeller-createAlert">
          <p className="mt-FormContactSeller-createAlertText">{textCreateAlert}</p>
          <SearchSave
            hasToCreateAlert={false}
            redirectTo={redirectTo}
            search={search}
            trackingOrigin={searchSaveTrackingOrigins.AFTER_CONTACT_AD_DETAIL}
            type={searchSaveTypes.contactSeller}
            showTooltip={false}
          />
        </div>
      )}
      <div className="mt-FormContactSeller-pta">
        <SellerPanel />
      </div>
    </div>
  )
}

ContactSellerConfirmation.propTypes = {}

export default ContactSellerConfirmation
